import Pusher from "pusher-js";
import localService from "../services/localService";
import { vm } from "../main";
import { DISABLE_LEGACY_MSHIFT_QUOTING } from "@/config/constants";

Pusher.logToConsole = false;
let debounce = null;
const _pusher = new Pusher(process.env.VUE_APP_PUSHER_API_KEY, {
  cluster: process.env.VUE_APP_PUSHER_CLUSTER,
});

function applyClientCompanySetupUpdated(vm, data) {
  vm.$nextTick(() => {
    const orgId = data?.organization_id;
    const selectedOrgId = vm.$store.state.broker?.selectedClient?.organization_id;
    if (!orgId || !selectedOrgId || String(orgId) !== String(selectedOrgId)) return;
    if (data?.company_setup) {
      vm.$store.commit("companySetup/setClientCompanySetup", data.company_setup);
    }
  });
}

export default {
  isRegistered: false,
  registerChannels: function () {
    // Public Channel
    const public_channel = _pusher.subscribe("public_channel");

    public_channel.bind("logoutAllUsers", function (data) {
      vm.$store.dispatch("login/logout", {});
    });

    // Private Channel
    const userInfo = localService.getItem("userInfo");
    if (userInfo) {
      const user = userInfo["user"];
      if (user?.entity_id) {
        const channel = _pusher.subscribe(user.entity_id.replaceAll("|", ""));

        channel.bind("logoutUser", function (data) {
          vm.$store.dispatch("login/logout", {});
        });

        channel.bind("updateAllowance", function (data) {
          vm.$store.dispatch("broker/updateAllowance", data);
        });

        if (user.admin_pusher_channel_id) {
          const super_admin_channel = _pusher.subscribe(user.admin_pusher_channel_id);

          // chunked-adminUpdateBrokerMembers feeds the broker SIDE PANEL
          // (admin/updateSelectedBroker), not the brokers list. The backend
          // continues to emit this for live side-panel updates, so it stays.
          const superAdminChunkedEvents = {};
          super_admin_channel.bind("chunked-adminUpdateBrokerMembers", function (data) {
            vm.$nextTick(() => {
              const { message_id, chunk_data, is_final_chunk } = data;
              const superAdminChunkedEvent =
                superAdminChunkedEvents[message_id] ||
                (superAdminChunkedEvents[message_id] = { chunkedData: [], receivedFinal: false });

              superAdminChunkedEvent.chunkedData.push(chunk_data);
              superAdminChunkedEvent.receivedFinal = is_final_chunk ? true : superAdminChunkedEvent.receivedFinal;

              if (superAdminChunkedEvent.receivedFinal) {
                superAdminChunkedEvent.chunkedData = JSON.parse(superAdminChunkedEvent.chunkedData.join(""));
                vm.$store.commit("admin/updateSelectedBroker", superAdminChunkedEvent.chunkedData);
                delete superAdminChunkedEvent[message_id];
              }
            });
          });

          // Real-time Customer Pipeline updates: backend broadcasts the full
          // company row on the super-admin channel for every create/update.
          super_admin_channel.bind("companyCreated", function (data) {
            vm.$store.dispatch("admin/pusherCompanyCreated", data);
          });

          super_admin_channel.bind("companyUpdated", function (data) {
            vm.$store.dispatch("admin/pusherCompanyUpdated", data);
          });

          super_admin_channel.bind("companyDeleted", function (data) {
            vm.$store.dispatch("admin/pusherCompanyDeleted", data);
          });

          // Brokerage CRUD events (full row on create/update; { entity_id } on delete).
          super_admin_channel.bind("brokerageCreated", function (data) {
            vm.$store.dispatch("admin/pusherBrokerageCreated", data);
          });

          super_admin_channel.bind("brokerageUpdated", function (data) {
            vm.$store.dispatch("admin/pusherBrokerageUpdated", data);
          });

          super_admin_channel.bind("brokerageDeleted", function (data) {
            vm.$store.dispatch("admin/pusherBrokerageDeleted", data);
          });

          // Broker CRUD events (broker-move emits on BOTH old + new brokerage
          // channels in addition to super-admin).
          super_admin_channel.bind("brokerCreated", function (data) {
            vm.$store.dispatch("admin/pusherBrokerCreated", data);
          });

          super_admin_channel.bind("brokerUpdated", function (data) {
            vm.$store.dispatch("admin/pusherBrokerUpdated", data);
          });

          super_admin_channel.bind("brokerDeleted", function (data) {
            vm.$store.dispatch("admin/pusherBrokerDeleted", data);
          });

          // Delegation (contributor) events.
          super_admin_channel.bind("delegationCreated", function (data) {
            vm.$store.dispatch("admin/pusherDelegationCreated", data);
          });

          super_admin_channel.bind("delegationDeleted", function (data) {
            vm.$store.dispatch("admin/pusherDelegationDeleted", data);
          });

          // Local-office team-member events.
          super_admin_channel.bind("localOfficeTeamMemberCreated", function (data) {
            vm.$store.dispatch("admin/pusherLocalOfficeTeamMemberCreated", data);
          });

          super_admin_channel.bind("localOfficeTeamMemberUpdated", function (data) {
            vm.$store.dispatch("admin/pusherLocalOfficeTeamMemberUpdated", data);
          });

          super_admin_channel.bind("localOfficeTeamMemberDeleted", function (data) {
            vm.$store.dispatch("admin/pusherLocalOfficeTeamMemberDeleted", data);
          });

          // Carrier settings events.
          super_admin_channel.bind("carrierSettingsUpdated", function (data) {
            vm.$store.dispatch("admin/pusherCarrierSettingsUpdated", data);
          });
        }

        if (user.brokerSidePanelSelectedClient) {
          // For BWB update quote params when CWB data updated
          const organization_channel = _pusher.subscribe(user.brokerSidePanelSelectedClient);
          organization_channel.bind("clientCompanySetupUpdated", function (data) {
            applyClientCompanySetupUpdated(vm, data);
          });
          const chunkedEvents = {};
          organization_channel.bind("chunked-updateCompanySetupGuide", function (data) {
            vm.$nextTick(() => {
              const { message_id, chunk_data, is_final_chunk } = data;
              const chunkedEvent =
                chunkedEvents[message_id] || (chunkedEvents[message_id] = { chunkedData: [], receivedFinal: false });

              chunkedEvent.chunkedData.push(chunk_data);

              chunkedEvent.receivedFinal = is_final_chunk ? true : chunkedEvent.receivedFinal;

              if (chunkedEvent.receivedFinal) {
                chunkedEvent.chunkedData = JSON.parse(chunkedEvent.chunkedData.join(""));

                const { questionnaire } = chunkedEvent.chunkedData;

                if (questionnaire && Object.keys(questionnaire).length) {
                  let data = chunkedEvent.chunkedData;
                  if (data.questionnaire) {
                    let questionnaire = data.questionnaire?.policiesPlanningQuestions?.questions;
                    let policyDetails = questionnaire?.find((e) => e.key == "policy_details_upload_policy");
                    if (policyDetails) {
                      let payload = {
                        coverage_trying_to_acquire:
                          policyDetails.formData.renewal_new_common_answers.coverage_trying_to_acquire,
                        deductible_interest_having:
                          policyDetails.formData.renewal_new_common_answers.deductible_interest_having,
                        effective_looking_date:
                          policyDetails.formData.renewal_new_common_answers.effective_looking_date,
                      };
                      vm.$store.commit("companySetup/updateClientCompanySetup", payload);
                    }
                  }
                }

                delete chunkedEvents[message_id];
              }
            });
          });
        }

        if (user.organization_id) {
          const organization_channel = _pusher.subscribe(user.organization_id);

          organization_channel.bind("updateQuoteSignStatus", function (data) {
            if (DISABLE_LEGACY_MSHIFT_QUOTING) return;
            vm.$store.commit("mShiftUser/updateQuoteSignStatus", data);
          });

          organization_channel.bind("updateOrganizationBrokerAllocations", function (data) {
            vm.$store.dispatch("broker/updateOrganizationBrokerAllocations", data);
          });

          // purchaseTokens happens when a seller sells tokens to a brokerage
          organization_channel.bind("purchaseTokens", function (data) {
            vm.$store.dispatch("broker/updatePurchasedTokens", data);
          });

          organization_channel.bind("updateAnswer", function (data) {
            let { assessment_id, entity_id } = vm.$route.query;
            if (assessment_id && entity_id) {
              clearTimeout(debounce);
              debounce = setTimeout(() => {
                vm.$store.dispatch("audit/fetchControlCertification", { withoutLoading: true });
              }, 5000);
            }
          });

          organization_channel.bind("updateClients", function (data) {
            vm.$store.dispatch("client/updateOrganizationClients", data);
          });

          organization_channel.bind("updateOrganizationClients", function (data) {
            vm.$store.dispatch("digitalAssets/updateOrganizationClients", data);
          });

          // updateDigitalAssets
          organization_channel.bind("updateDigitalAssets", function (data) {
            data.map((x) => (x.checked = false));
            vm.$store.dispatch("digitalAssets/updateDigitalAssets", data);
          });

          // deleteOrganizationClients
          organization_channel.bind("deleteOrganizationClients", function (data) {
            vm.$store.dispatch("digitalAssets/deleteOrganizationClients", data);
            vm.$store.dispatch("client/deleteOrganizationClients", data);
          });

          organization_channel.bind("updateQuoteDetail", function (data) {
            if (DISABLE_LEGACY_MSHIFT_QUOTING) return;
            if (data?.detail) {
              let jsonData = { ...JSON.parse(data.detail) };
              jsonData.quote_type = data.quote_type;
              vm.$store.commit("mShiftUser/updateQuoteDetail", jsonData);
            }
          });

          organization_channel.bind("updateAttachments", function (data) {
            vm.$store.dispatch("files/updateAttachments", data);
          });

          organization_channel.bind("updateBrokerMembers", function (data) {
            if (data.some((e) => e.broker_id == user.entity_id)) {
              vm.$store.dispatch("broker/updateBrokerMembers", data);
            }
          });

          // Real-time Members list updates for the broker: backend broadcasts
          // the full company row on the affected broker's org channel.
          organization_channel.bind("companyCreated", function (data) {
            vm.$store.dispatch("broker/pusherCompanyCreated", data);
          });

          organization_channel.bind("companyUpdated", function (data) {
            vm.$store.dispatch("broker/pusherCompanyUpdated", data);
          });

          organization_channel.bind("companyDeleted", function (data) {
            vm.$store.dispatch("broker/pusherCompanyDeleted", data);
          });

          // Broker-team CRUD events on the brokerage's org channel — the broker
          // viewing their own team sees colleagues join / leave / get moved in
          // real time. Backend emits to BOTH old + new brokerage channels on
          // a broker-move so the previous brokerage's view also reconciles.
          organization_channel.bind("brokerCreated", function (data) {
            vm.$store.dispatch("broker/pusherBrokerCreated", data);
          });

          organization_channel.bind("brokerUpdated", function (data) {
            vm.$store.dispatch("broker/pusherBrokerUpdated", data);
          });

          organization_channel.bind("brokerDeleted", function (data) {
            vm.$store.dispatch("broker/pusherBrokerDeleted", data);
          });

          // Delegation (contributor) events for the broker's own brokerage.
          organization_channel.bind("delegationCreated", function (data) {
            vm.$store.dispatch("broker/pusherDelegationCreated", data);
          });

          organization_channel.bind("delegationDeleted", function (data) {
            vm.$store.dispatch("broker/pusherDelegationDeleted", data);
          });

          // Local-office team-member events.
          organization_channel.bind("localOfficeTeamMemberCreated", function (data) {
            vm.$store.dispatch("broker/pusherLocalOfficeTeamMemberCreated", data);
          });

          organization_channel.bind("localOfficeTeamMemberUpdated", function (data) {
            vm.$store.dispatch("broker/pusherLocalOfficeTeamMemberUpdated", data);
          });

          organization_channel.bind("localOfficeTeamMemberDeleted", function (data) {
            vm.$store.dispatch("broker/pusherLocalOfficeTeamMemberDeleted", data);
          });

          // Carrier settings updated for the broker's own brokerage.
          organization_channel.bind("carrierSettingsUpdated", function (data) {
            vm.$store.dispatch("broker/pusherCarrierSettingsUpdated", data);
          });

          organization_channel.bind("clientCompanySetupUpdated", function (data) {
            applyClientCompanySetupUpdated(vm, data);
          });

          // V2 submission / quote realtime events. Backend emits to the
          // brokerage's organization_id (i.e. this same channel) using the
          // SAME camelCase row shape the REST endpoints return, so the store
          // can upsert without an extra mapping step.
          organization_channel.bind("submissionCreated", function (data) {
            vm.$store.dispatch("brokerSubmission/pusherSubmissionCreated", data);
            const orgId = data?.organizationId || data?.organization_id;
            if (orgId) {
              vm.$store.dispatch("broker/refreshBrokerSidePanelCompanySetup", orgId);
            }
          });

          organization_channel.bind("submissionUpdated", function (data) {
            vm.$store.dispatch("brokerSubmission/pusherSubmissionUpdated", data);
            const orgId = data?.organizationId || data?.organization_id;
            if (orgId) {
              vm.$store.dispatch("broker/refreshBrokerSidePanelCompanySetup", orgId);
            }
          });

          organization_channel.bind("quoteCreated", function (data) {
            vm.$store.dispatch("brokerSubmission/pusherQuoteCreated", data);
          });

          organization_channel.bind("quoteUpdated", function (data) {
            vm.$store.dispatch("brokerSubmission/pusherQuoteUpdated", data);
          });

          // Coverage comparison matrix cell / analysis-status patches.
          organization_channel.bind("comparisonUpdated", function (data) {
            vm.$store.dispatch("coverageComparison/onPusherComparisonUpdated", data);
          });

          // Coverage-analysis run finished (and advisory regenerate-before-emit).
          // Soft-refresh comparison + advisory when an open compare view is cached.
          organization_channel.bind("coverageAnalysisUpdated", function (data) {
            vm.$store.dispatch("coverageComparison/onPusherCoverageAnalysisUpdated", data);
          });

          // S2BWB-344: a submission's scan summary changed status. Re-read only when this
          // tab holds that submission's summary.
          organization_channel.bind("scanSummaryUpdated", function (data) {
            vm.$store.dispatch("scanSummary/onPusherScanSummaryUpdated", data);
          });

          // Submission / carrier chat messages: broker outbound, carrier
          // inbound (SHAPE webhook), and system events all emit on this same
          // event. The store appends to whichever buckets are loaded.
          organization_channel.bind("submissionMessageCreated", function (data) {
            vm.$store.dispatch("brokerSubmission/pusherSubmissionMessageCreated", data);
          });

          // Shape email-origin submissions staged for broker review. The badge
          // count + (when loaded) the Review list are kept live off these
          // events so the temporary Review nav appears/disappears in real time.
          // Event names match S2R BE; there is no separate "resolved" event —
          // a resolution arrives as pendingReviewUpdated (status no longer
          // pending) plus an authoritative pendingReviewCountChanged.
          organization_channel.bind("pendingReviewCreated", function (data) {
            vm.$store.dispatch("brokerReview/pusherPendingReviewCreated", data);
          });

          organization_channel.bind("pendingReviewUpdated", function (data) {
            vm.$store.dispatch("brokerReview/pusherPendingReviewUpdated", data);
          });

          // Authoritative badge count push (number or { count }).
          organization_channel.bind("pendingReviewCountChanged", function (data) {
            vm.$store.dispatch("brokerReview/pusherPendingReviewCountChanged", data);
          });

          const chunkedEvents = {};
          organization_channel.bind("chunked-updateCompanySetupGuide", function (data) {
            vm.$nextTick(() => {
              const { message_id, chunk_data, is_final_chunk } = data;

              const chunkedEvent =
                chunkedEvents[message_id] || (chunkedEvents[message_id] = { chunkedData: [], receivedFinal: false });

              chunkedEvent.chunkedData.push(chunk_data);

              chunkedEvent.receivedFinal = is_final_chunk ? true : chunkedEvent.receivedFinal;

              if (chunkedEvent.receivedFinal) {
                chunkedEvent.chunkedData = JSON.parse(chunkedEvent.chunkedData.join(""));

                const { questionnaire } = chunkedEvent.chunkedData;

                if (questionnaire && Object.keys(questionnaire).length) {
                  vm.$store.dispatch("companySetup/updateQuestionnaireDataByQuestion", chunkedEvent.chunkedData);
                }

                delete chunkedEvents[message_id];
              }
            });
          });

          organization_channel.bind("updateAlphaSetupGuide", function (data) {
            vm.$nextTick(() => {
              if (data.questionnaire) {
                vm.$store.dispatch("alpha/updateAlphaSetup", data);
              }
            });
          });

          organization_channel.bind("updateQuotesSubmission", function (data) {
            if (DISABLE_LEGACY_MSHIFT_QUOTING) return;
            const organization_id = vm.$store.state.broker?.selectedClient?.organization_id;
            if (!organization_id || data?.organization_id != organization_id) return;

            if (data.status === "GeneratingQuotes") {
              vm.$store.commit("mShiftUser/setQuickQuoteToGenerating");
            }

            // RUNS WHEN QUICK QUOTE
            if (data.quote_type === "quick_quotes") {
              vm.$store.commit("mShiftUser/updateQuoteStatus", {
                quick_quotes_submission: data,
              });
              if (data.status !== "GeneratingQuotes") {
                vm.$store.dispatch("mShiftUser/getMShiftQuotes", { organization_id });
              }
            }

            // RUNS WHEN FULL QUOTE
            if (data.quote_type === "full_quotes") {
              vm.$store.commit("mShiftUser/updateFullQuoteStatus", {
                full_quotes_submission: data,
                ready_for_full_quote: true,
              });
              if (data.status !== "GeneratingQuotes") {
                vm.$store.dispatch("mShiftUser/getFullQuotes", { organization_id });
              }
            }
          });

          organization_channel.bind("updateApplications", function (data) {
            vm.$store.commit("applications/UPDATE_APPLICATIONS", data);
          });

          organization_channel.bind("updatePrivacyReportsData", function (data) {
            vm.$store.commit("privacy/UPDATE_PRIVACY_REPORT_DATA", data);
          });

          organization_channel.bind("updatePrivacyReports", function (data) {
            vm.$store.commit("privacy/UPDATE_PRIVACY_PROPERTIES", data);
          });

          organization_channel.bind("updateThreatReportsData", function (data) {
            vm.$store.commit("threats/UPDATE_THREAT_REPORT_DATA", data);
          });

          organization_channel.bind("updateThreatReports", function (data) {
            vm.$store.commit("threats/UPDATE_THREAT_PROPERTIES", data);
          });

          organization_channel.bind("updateLocalOfficeSetup", function (data) {
            vm.$store.commit("localOffice/updateLocalOfficeSetup", data);
          });

          organization_channel.bind("updateBrokerCarrierSettings", function (data) {
            if (data?.carrier_settings) {
              vm.$store.commit("carrierSettings/SET_BROKER_SETTINGS", data.carrier_settings);
            }
          });

          organization_channel.bind("updateInsurabilityReports", function (data) {
            vm.$store.commit("insurability/UPDATE_REPORTS_DATA", data);
          });
        }

        this.isRegistered = true;
      }
    }
  },
};
